import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";

const baseUrl = import.meta.env.VITE_APP_BASE_URL;

const levelToColor = {
  Beginner: "bg-orange-100 text-orange-700",
  Developing: "bg-orange-100 text-orange-700",
  Proficient: "bg-green-100 text-green-700",
  Advanced: "bg-green-100 text-green-700",
  Mastery: "bg-purple-100 text-purple-700",
};

const barColor = {
  Beginner: "bg-orange-400",
  Developing: "bg-orange-400",
  Proficient: "bg-teal-500",
  Advanced: "bg-teal-500",
  Mastery: "bg-purple-500",
};

const getLevelWidth = (level) => {
  const map = {
    Beginner: 20,
    Developing: 40,
    Proficient: 60,
    Advanced: 80,
    Mastery: 100,
  };
  return map[level] || 0;
};

const StrengthAreas = () => {
  const [strengthAreas, setStrengthAreas] = useState({});
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStrengthAreas = async () => {
      try {
        setLoading(true);
        const res = await axios.get(
          `${baseUrl}/progress/progress2/strength-areas`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );
        const fetchedStrengths = res.data.strengthAreas || {};
        setStrengthAreas(fetchedStrengths);
        const subjects = Object.keys(fetchedStrengths);
        setSelectedSubject((prev) => {
          if (prev && fetchedStrengths[prev]) {
            return prev;
          }
          return subjects[0] || "";
        });
      } catch (error) {
        console.error("Error fetching strength areas:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchStrengthAreas();
  }, []);

  const subjects = Object.keys(strengthAreas);
  const selectedTopics = strengthAreas[selectedSubject] || {};
  const selectedTopicEntries = Object.entries(selectedTopics);
  const navigateToAssessment = useCallback(() => {
    const subjectParam = selectedSubject
      ? `?subject=${encodeURIComponent(selectedSubject)}`
      : "";
    window.location.href = `/assessment${subjectParam}`;
  }, [selectedSubject]);

  if (loading) {
    return (
      <div className="min-h-screen p-6 font-sans bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
          <p className="text-gray-600 text-lg">Loading strength areas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen p-6 font-sans bg-gray-50">
      {subjects.length === 0 ? (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-8 text-center">
          <h2 className="text-2xl font-semibold text-gray-800 mb-4">
            No subjects available yet
          </h2>
          <p className="text-gray-600 mb-6">
            Complete an assessment to see your strength areas by subject.
          </p>
          <button
            onClick={navigateToAssessment}
            className="text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
            style={{
              backgroundColor: "#FF6B00",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#E55A00";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#FF6B00";
            }}
          >
            Go to Self Assessment
          </button>
        </div>
      ) : (
        <div className="space-y-6">
          <div className="w-full flex justify-center">
            <div className="flex gap-2 md:gap-3 overflow-x-auto -mx-2 px-2 md:mx-0 md:px-0 justify-center items-center bg-gray-100 rounded-full">
              {subjects.map((subject) => (
                <button
                  key={subject}
                  onClick={() => setSelectedSubject(subject)}
                  className={`px-4 md:px-6 py-2 md:py-3 rounded-full text-sm md:text-base font-medium whitespace-nowrap transition-all duration-200 flex-shrink-0 ${
                    selectedSubject === subject
                      ? "bg-[#0D99FF] text-white shadow-md"
                      : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                  }`}
                >
                  {subject}
                </button>
              ))}
            </div>
          </div>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between gap-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center">
                <span className="mr-3">{selectedSubject}</span>
                <span className="text-sm text-gray-500 font-normal">
                  ({selectedTopicEntries.length}{" "}
                  {selectedTopicEntries.length === 1 ? "topic" : "topics"})
                </span>
              </h2>
              {selectedTopicEntries.length > 0 && (
                <button
                  onClick={navigateToAssessment}
                  className="text-sm font-semibold text-white px-4 py-2 rounded-full bg-[#0D99FF] hover:bg-[#0b82d6] transition-colors"
                >
                  Go to Self Assessment
                </button>
              )}
            </div>
            <div className="p-6">
              {selectedTopicEntries.length === 0 ? (
                <div className="text-center py-12">
                  <div className="mb-6">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <svg
                        className="w-8 h-8 text-gray-400"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">
                      Missing skill for the subject {selectedSubject}
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Please set your level for the subject.
                    </p>
                  </div>
                  <button
                    onClick={navigateToAssessment}
                    className="text-white font-semibold py-3 px-8 rounded-lg shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105"
                    style={{
                      backgroundColor: "#FF6B00",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = "#E55A00";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = "#FF6B00";
                    }}
                  >
                    Go to Self Assessment
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {selectedTopicEntries.map(([topic, topicData], idx) => (
                    <div
                      key={`${selectedSubject}-${topic}-${idx}`}
                      className="bg-gray-50 rounded-lg p-4 border border-gray-200"
                    >
                      <h3 className="text-lg font-semibold text-gray-800 mb-2">
                        {topic}
                      </h3>

                      <div className="w-full h-6 bg-gray-200 rounded-full mb-3 overflow-hidden">
                        <div
                          className={`h-full text-white text-sm font-semibold text-center flex items-center justify-center ${
                            barColor[topicData.level]
                          }`}
                          style={{
                            width: `${getLevelWidth(topicData.level)}%`,
                          }}
                        >
                          {topicData.level}
                        </div>
                      </div>

                      <div className="flex justify-between text-sm text-gray-500 font-medium mb-3">
                        <span>Beginner</span>
                        <span>Advanced</span>
                        <span>Mastery</span>
                      </div>

                      <hr className="mb-3" />

                      <div>
                        <h4 className="text-base font-semibold text-gray-700 mb-2">
                          Subtopics:
                        </h4>
                        {topicData.subtopics &&
                          Object.entries(topicData.subtopics).map(
                            ([subName, subLevel], i) => (
                              <div
                                key={i}
                                className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0"
                              >
                                <span className="text-sm text-gray-800">
                                  {subName}
                                </span>
                                <span
                                  className={`text-sm font-semibold px-3 py-1 rounded-full ${levelToColor[subLevel]}`}
                                >
                                  {subLevel}
                                </span>
                              </div>
                            )
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StrengthAreas;
